<script setup>
import { ref, computed } from 'vue';
import { Eye, EyeOff, X } from 'lucide-vue-next';

const model = defineModel({ type: [String, Number], default: '' });
const props = defineProps({
  id: { type: String, default: null },
  type: { type: String, default: 'text' },
  placeholder: { type: String, default: '' },
  /** Fixed text in a separated cell before the input, for example "+91" */
  prefix: { type: String, default: '' },
  /** Fixed text after the input, for example "INR" */
  suffix: { type: String, default: '' },
  icon: { type: [Object, Function], default: null },
  /** md or lg (huge amount style) */
  size: { type: String, default: 'md' },
  invalid: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  clearable: { type: Boolean, default: false },
  uppercase: { type: Boolean, default: false },
  inputmode: { type: String, default: null },
  maxlength: { type: [Number, String], default: null },
  autocomplete: { type: String, default: null },
});
const reveal = ref(false);
const realType = computed(() => (props.type === 'password' && reveal.value ? 'text' : props.type));
const onInput = (e) => { model.value = props.uppercase ? e.target.value.toUpperCase() : e.target.value; };
</script>

<template>
  <div
    class="pd-input"
    :class="[`pd-input--${size}`, { 'is-invalid': invalid, 'is-disabled': disabled, 'is-readonly': readonly, 'has-prefix': prefix }]"
  >
    <span v-if="prefix" class="pd-input__prefix">{{ prefix }}</span>
    <component :is="icon" v-if="icon" class="pd-input__icon" :size="size === 'lg' ? 26 : 18" :stroke-width="2.25" aria-hidden="true" />
    <input
      :id="id"
      class="pd-input__el"
      :type="realType"
      :value="model"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :inputmode="inputmode"
      :maxlength="maxlength"
      :autocomplete="autocomplete"
      :aria-invalid="invalid || undefined"
      @input="onInput"
    />
    <button v-if="clearable && model" type="button" class="pd-input__btn" aria-label="Clear" @click="model = ''">
      <X :size="16" aria-hidden="true" />
    </button>
    <button
      v-if="type === 'password'"
      type="button"
      class="pd-input__btn"
      :aria-label="reveal ? 'Hide password' : 'Show password'"
      @click="reveal = !reveal"
    >
      <component :is="reveal ? EyeOff : Eye" :size="18" aria-hidden="true" />
    </button>
    <span v-if="suffix" class="pd-input__suffix">{{ suffix }}</span>
    <slot name="action" />
  </div>
</template>

<style scoped>
.pd-input {
  --h: 52px;
  display: flex;
  align-items: center;
  min-height: var(--h);
  background: var(--pd-white);
  border: 1px solid var(--pd-line);
  border-radius: var(--pd-r-sm);
  transition: border-color var(--pd-fast) var(--pd-ease), box-shadow var(--pd-fast) var(--pd-ease);
}
.pd-input:focus-within { border-color: var(--pd-line-active); box-shadow: var(--pd-ring); }
.pd-input.is-invalid { border-color: var(--pd-danger); }
.pd-input.is-disabled, .pd-input.is-readonly { background: var(--pd-cream); }
.pd-input--lg { --h: 76px; border-radius: var(--pd-r-md); }
.pd-input__el {
  flex: 1;
  min-width: 0;
  height: calc(var(--h) - 3px);
  padding: 0 16px;
  border: 0;
  background: transparent;
  font-weight: 650;
  font-size: 1rem;
  outline: none;
}
.pd-input__el::placeholder { color: var(--pd-subtle); font-weight: 500; }
.pd-input--lg .pd-input__el { font-size: 2rem; font-weight: 800; letter-spacing: -0.03em; font-variant-numeric: tabular-nums; }
.pd-input__icon { margin-left: 16px; color: var(--pd-ink); }
.pd-input__icon + .pd-input__el { padding-left: 10px; }
.pd-input__prefix, .pd-input__suffix {
  display: grid;
  place-items: center;
  align-self: stretch;
  padding: 0 16px;
  font-weight: 800;
}
.pd-input__prefix { border-right: 1px solid var(--pd-line); }
.pd-input__suffix { color: var(--pd-muted); font-size: 0.8125rem; letter-spacing: 0.08em; }
.pd-input__btn { display: grid; place-items: center; width: 40px; height: 40px; margin-right: 6px; border: 0; background: none; color: var(--pd-muted); cursor: pointer; border-radius: 50%; }
.pd-input :deep(.pd-btn) { margin-right: 6px; }
</style>
